import { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute, useLocation, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, CircleCheck, Download, Save, Sparkles, Trash2, Upload } from "lucide-react";
import { Badge } from "@tubo/ui/components/ui/badge";
import { Button } from "@tubo/ui/components/ui/button";
import { Textarea } from "@tubo/ui/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@tubo/ui/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@tubo/ui/components/ui/alert-dialog";
import {
  useValueVocabularyDetail,
  useUpdateItems,
  useImportCsv,
  useExportCsv,
  usePublishVocabulary,
  getValueVocabularyDetailQueryKey,
  getValueVocabularyListQueryKey,
} from "@tubo/api-models/hooks/value-vocabulary-api.hooks";
import { useSemanticConceptList } from "@tubo/api-models/hooks/semantic-concept-api.hooks";
import type { ConceptLinkSuggestionVO, VocabularyItemVO } from "@tubo/api-models/hooks/value-vocabulary-api.schemas";
import { VocabularyItemStatusEnum } from "@tubo/api-models/business/semantic/semantic.enums";
import {
  useSecondaryNavActions,
  useSecondaryNavTitle,
} from "@client/components/secondary-nav";
import { ConceptLinkSelectDialog } from "@client/components/semantic-platform/concept-link-select-dialog";
import { VocabularyConceptSuggestDialog } from "@client/components/semantic-platform/vocabulary-concept-suggest-dialog";
import { VocabularyBasicInfoPanel } from "@client/components/semantic-platform/vocabulary-basic-info-panel";
import { VocabularyDeleteDialog } from "@client/components/semantic-platform/vocabulary-delete-dialog";
import { VocabularyItemsTable } from "@client/components/semantic-platform/vocabulary-items-table";
import { publishStatusLabel, publishStatusVariant } from "@client/components/semantic-platform/publish-status";

export const Route = createFileRoute("/_app/semantic-platform/vocabularies/$id")({
  component: VocabularyDetailPage,
});

/** 生成词条 ID:UUID(全局唯一;code 仅作内部唯一键,展示与父级选择均走显示名) */
function generateItemCode(): string {
  return crypto.randomUUID();
}

function VocabularyDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { searchStr } = useLocation();
  const listSearch = new URLSearchParams(searchStr).get("ns");
  const queryClient = useQueryClient();
  const { data: vocab } = useValueVocabularyDetail(id, { query: {} });
  const { data: csvText } = useExportCsv(id, { query: {} });

  // 本地编辑态(items 整组提交;服务端环/自反校验)
  const [items, setItems] = useState<VocabularyItemVO[]>([]);
  const [importOpen, setImportOpen] = useState(false);
  const [csvDraft, setCsvDraft] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [itemDeleteIdx, setItemDeleteIdx] = useState<number | null>(null);
  // 概念挂接(P2a 手动挂接 / P3 AI 推荐)
  const [pickIdx, setPickIdx] = useState<number | null>(null);
  const [suggestOpen, setSuggestOpen] = useState(false);

  const { data: conceptPage } = useSemanticConceptList(
    { page: 1, pageSize: 500 },
    { query: {} },
  );
  const conceptLabelByCode = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of conceptPage?.items ?? []) map.set(c.code, c.preferredLabel);
    return map;
  }, [conceptPage]);

  useEffect(() => {
    if (vocab?.items) setItems(vocab.items);
  }, [vocab]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: getValueVocabularyDetailQueryKey(id) });

  const { mutate: saveItems, isPending: savingItems } = useUpdateItems({
    mutation: {
      onSuccess: () => { invalidate(); toast.success("字典项已保存"); },
      onError: () => toast.error("保存失败(ID 重复/父级不存在/层级环)"),
    },
  });
  const publishMutation = usePublishVocabulary({
    mutation: {
      onSuccess: (detail) => {
        invalidate();
        queryClient.invalidateQueries({ queryKey: getValueVocabularyListQueryKey() });
        toast.success(`「${detail?.name ?? "数据字典"}」已发布（抽取字段绑定与值归一仅消费已发布词表）`);
      },
      onError: () => toast.error("发布失败"),
    },
  });
  const importMutation = useImportCsv({
    mutation: {
      onSuccess: (res) => {
        invalidate();
        toast.success(`导入完成:新建 ${res.created},更新 ${res.updated},跳过 ${res.skipped}`);
        setImportOpen(false);
        setCsvDraft("");
      },
      onError: () => toast.error("导入失败(CSV 格式或内容非法)"),
    },
  });

  const dirty = vocab ? JSON.stringify(items) !== JSON.stringify(vocab.items ?? []) : false;

  const addItem = () =>
    setItems((prev) => [...prev, { code: generateItemCode(), preferredLabel: "", sortOrder: prev.length + 1 }]);
  const patchItem = (idx: number, patch: Partial<VocabularyItemVO>) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const removeItem = (idx: number) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const toggleItemStatus = (idx: number) =>
    patchItem(idx, {
      status:
        items[idx]?.status === VocabularyItemStatusEnum.Deprecated
          ? VocabularyItemStatusEnum.Active
          : VocabularyItemStatusEnum.Deprecated,
    });
  const clearConcept = (idx: number) => patchItem(idx, { conceptCode: undefined });
  const applySuggestions = (picked: ConceptLinkSuggestionVO[]) => {
    const conceptByItemCode = new Map(picked.map((s) => [s.itemCode, s.conceptCode]));
    setItems((prev) => prev.map((it) => {
      const conceptCode = conceptByItemCode.get(it.code);
      return conceptCode ? { ...it, conceptCode } : it;
    }));
    toast.success(`已写入 ${picked.length} 条挂接,点击「保存字典项」生效`);
  };
  const unlinkedCount = items.filter(
    (i) => !i.conceptCode && i.status !== VocabularyItemStatusEnum.Deprecated,
  ).length;

  const downloadCsv = useCallback(() => {
    if (!csvText || !vocab) return;
    const blob = new Blob([csvText], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${vocab.code}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [csvText, vocab]);

  // 头部注入域级 SecondaryNav(标题区:返回/ID/名称;操作区:导入导出/保存/删除),页面体不再自写标题栏
  const headerTitle = useMemo(
    () => (
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon-sm" aria-label="返回列表" onClick={() => navigate({ to: "/semantic-platform/vocabularies", search: listSearch ? { ns: listSearch } : {} })}>
          <ArrowLeft className="size-4" />
        </Button>
        {vocab ? (
          <>
            <span className="font-mono text-sm text-muted-foreground">{vocab.code}</span>
            <span className="text-sm font-semibold">{vocab.name}</span>
            {vocab.isHierarchical && <Badge variant="outline">层级</Badge>}
            <Badge variant={publishStatusVariant(vocab.status)}>{publishStatusLabel(vocab.status)}</Badge>
          </>
        ) : (
          <span className="text-sm font-semibold">数据字典</span>
        )}
      </div>
    ),
    [vocab, navigate, listSearch],
  );
  useSecondaryNavTitle(headerTitle);

  const headerActions = useMemo(
    () =>
      vocab ? (
        <div className="flex items-center gap-2">
          {vocab.status !== "published" && (
            <Button variant="outline" size="sm" disabled={publishMutation.isPending} onClick={() => publishMutation.mutate({ id })}>
              <CircleCheck className="mr-1 size-4" /> 发布
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}>
            <Upload className="mr-1 size-4" /> CSV 导入
          </Button>
          <Button variant="outline" size="sm" onClick={downloadCsv}>
            <Download className="mr-1 size-4" /> CSV 导出
          </Button>
          <Button size="sm" disabled={!dirty || savingItems} onClick={() => saveItems({ id, data: { items } })}>
            <Save className="mr-1 size-4" /> 保存字典项
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="删除字典"
            title="删除字典"
            className="text-destructive"
            onClick={() => setDeleteOpen(true)}
          >
            <Trash2 className="size-3.5" />
          </Button>
        </div>
      ) : null,
    [vocab, dirty, savingItems, saveItems, downloadCsv, id, items, publishMutation],
  );
  useSecondaryNavActions(headerActions);

  if (!vocab) {
    return <div className="flex h-full items-center justify-center text-muted-foreground">加载中…</div>;
  }

  const itemDeleteTarget = itemDeleteIdx !== null ? items[itemDeleteIdx] : null;

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-auto p-4 xl:p-6">
        <div className="space-y-4">
          <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_20rem]">
            <VocabularyBasicInfoPanel vocabulary={vocab} onSaved={invalidate} />

            <section className="flex h-full min-h-0 flex-col rounded-lg border bg-surface/50 p-4">
              <h2 className="mb-3 text-sm font-semibold">enumOptions 投影预览</h2>
              <p className="mb-2 text-xs text-muted-foreground">
                与旧抽取器消费的扁平 string[] 同构(启用状态的项的首选名,按排序;已弃用项不进入投影)
              </p>
              <div className="min-h-0 flex-1 space-y-1 overflow-auto rounded bg-muted/40 p-2 font-mono text-xs">
                {(vocab.enumOptions ?? []).map((opt) => (
                  <div key={opt} className="px-1">"{opt}"</div>
                ))}
                {(vocab.enumOptions ?? []).length === 0 && <span className="text-muted-foreground">[]</span>}
              </div>
            </section>
          </div>

          <VocabularyItemsTable
            items={items}
            hierarchical={Boolean(vocab.isHierarchical)}
            conceptLabelByCode={conceptLabelByCode}
            onPickConcept={setPickIdx}
            onClearConcept={clearConcept}
            actions={
              <Button
                variant="outline"
                size="sm"
                disabled={unlinkedCount === 0}
                title={unlinkedCount === 0 ? "没有未挂概念的值项" : "AI 扫描未挂值项并推荐候选概念"}
                onClick={() => setSuggestOpen(true)}
              >
                <Sparkles className="mr-1 size-4" /> AI 推荐挂接
              </Button>
            }
            onAdd={addItem}
            onPatch={patchItem}
            onToggleStatus={toggleItemStatus}
            onDelete={setItemDeleteIdx}
          />
        </div>
      </div>

      <ConceptLinkSelectDialog
        open={pickIdx !== null}
        onOpenChange={(open) => !open && setPickIdx(null)}
        onPick={(concept) => {
          if (pickIdx !== null) {
            patchItem(pickIdx, { conceptCode: concept.code });
            toast.success(`已挂接「${concept.preferredLabel}」,点击「保存字典项」生效`);
          }
        }}
      />

      <VocabularyConceptSuggestDialog
        vocabularyId={id}
        open={suggestOpen}
        onOpenChange={setSuggestOpen}
        onApply={applySuggestions}
      />

      <AlertDialog open={itemDeleteIdx !== null} onOpenChange={(open) => !open && setItemDeleteIdx(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>删除词条「{itemDeleteTarget?.preferredLabel || itemDeleteTarget?.code}」</AlertDialogTitle>
            <AlertDialogDescription>
              若该词条已被历史数据引用,建议改用「弃用」;删除在点击「保存字典项」后生效,保存前刷新页面可放弃更改。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => itemDeleteIdx !== null && removeItem(itemDeleteIdx)}
            >
              确认删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <VocabularyDeleteDialog
        target={deleteOpen ? { id: vocab.id, code: vocab.code, name: vocab.name } : null}
        onClose={() => setDeleteOpen(false)}
        onDeleted={() => navigate({ to: "/semantic-platform/vocabularies", search: listSearch ? { ns: listSearch } : {} })}
      />

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>CSV 导入(幂等:同 code 更新而非重复插入)</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">列:code(ID),preferredLabel(显示名),aliases(别名,; 分隔),parentItemCode(父级 ID),sortOrder(排序);首行表头。</p>
          <Textarea rows={8} value={csvDraft} onChange={(e) => setCsvDraft(e.target.value)} placeholder={"code,preferredLabel,aliases,parentItemCode,sortOrder\ntensile,拉伸,Tension,,1"} />
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setImportOpen(false)}>取消</Button>
            <Button size="sm" disabled={!csvDraft.trim() || importMutation.isPending} onClick={() => importMutation.mutate({ id, data: { csv: csvDraft } })}>
              导入
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
